import * as React from "react";
import useKBConnection from "../hooks/useKBConnection";
import UploadButton from "./UploadButton";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import useTeams from "@/hooks/useTeams";
import ColorPicker from "@/components/ColorPicker";
import {
  PrimaryBlueButton
} from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

export default function Index() {
  const { connected } = useKBConnection();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [companyName, setCompanyName] = React.useState("");
  const [themeColor, setThemeColor] = React.useState("");
  const [headerText, setHeaderText] = React.useState("");
  const [returnLinkText, setReturnLinkText] = React.useState("");
  const [returnLinkUrl, setReturnLinkUrl] = React.useState("");
  const [logoUrl, setLogoUrl] = React.useState("");
  const [enableLiveChat, setEnableLiveChat] = React.useState(false);
  const [liveChatGroupId, setLiveChatGroupId] = React.useState<number>(0);
  const [domain, setDomain] = React.useState("");
  const [showHelp, setShowHelp] = React.useState(false);
  const [urlCopied, setUrlCopied] = React.useState(false);
  const [searchEnabled, setSearchEnabled] = React.useState<boolean | null>(null);
  const [reindexing, setReindexing] = React.useState(false);
  const [reindexResult, setReindexResult] = React.useState<{ count: number; message: string } | null>(null);
  const [enableKbBotIndexing, setEnableKbBotIndexing] = React.useState(true);
  const [kbAutoReindexIntervalHours, setKbAutoReindexIntervalHours] = React.useState(24);
  const [hasAI, setHasAI] = React.useState(false);
  const { teams } = useTeams();
  const api = useApi();
  const toastAlert = useToastAlert();

  React.useEffect(() => {
    async function fetchTenant() {
      setLoading(true);
      try {
        const response = await api.admin.get("api/kb/settings");
        const json = await response.json();
        setCompanyName(json?.CompanyName ?? "");
        setThemeColor(json?.ThemeColor ?? "");
        setHeaderText(json?.HeaderText ?? "");
        setEnableLiveChat(json?.EnableLiveChat ?? false);
        setLiveChatGroupId(json?.LiveChatGroupId ?? 0);
        setReturnLinkText(json?.ReturnLinkText ?? "");
        setReturnLinkUrl(json?.ReturnLinkUrl ?? "");
        setLogoUrl(json?.LogoUrl ?? "");
        setDomain(json?.Domain ?? "");
        setEnableKbBotIndexing(json?.EnableKbBotIndexing ?? true);
        setKbAutoReindexIntervalHours(json?.KbAutoReindexIntervalHours ?? 24);
        setHasAI(json?.HasAI ?? false);
      } catch (err) {
        console.error("Failed to fetch KB settings:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchTenant();

    // Check if Azure Search is configured
    api.admin.get("api/kb/search/status").then(async (r) => {
      if (r.ok) {
        const data = await r.json();
        setSearchEnabled(data?.enabled ?? false);
      }
    }).catch(() => setSearchEnabled(false));
  }, []);

  async function handleSubmit() {
    setSaving(true);
    try {
      const resp = await api.admin.post("api/kb/settings", {
        companyName,
        headerText,
        themeColor,
        enableLiveChat,
        liveChatGroupId,
        returnLinkText,
        returnLinkUrl,
        logoUrl,
        enableKbBotIndexing
      });

      if (resp.status === 400) {
        toastAlert.displayToast(Severity.Error, "Failed to save settings");
      } else {
        toastAlert.displayToast(Severity.Success, "Settings saved");
      }
    } catch (err) {
      console.error("Failed to save KB settings:", err);
      toastAlert.displayToast(Severity.Error, "Failed to save settings");
    } finally {
      setSaving(false);
    }
  }

  async function triggerReindex() {
    setReindexing(true);
    setReindexResult(null);
    try {
      const resp = await api.admin.post("api/kb/search/reindex", {});
      const data = await resp.json();
      if (data?.success) {
        setReindexResult({ count: data.count, message: data.message });
        toastAlert.displayToast(Severity.Success, `Reindex complete — ${data.count} articles indexed`);
      } else {
        toastAlert.displayToast(Severity.Error, data?.message ?? "Reindex failed");
      }
    } catch (err) {
      console.error("Reindex error:", err);
      toastAlert.displayToast(Severity.Error, "Reindex failed");
    } finally {
      setReindexing(false);
    }
  }

  async function uploadFile(file: File) {
    try {
      const formData = new FormData();
      formData.append("logo", file);

      const response = await api.admin.postFormData("api/kb/settings/upload-logo", formData);
      const json = await response.json();
      setLogoUrl(json?.url ?? "");
      toastAlert.displayToast(Severity.Success, "Logo uploaded");
    } catch (err) {
      console.error("Failed to upload logo:", err);
      toastAlert.displayToast(Severity.Error, "Failed to upload logo");
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  if (connected === null) return null;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Sticky Save Bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm -mx-6 px-6 py-3 mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-800">
            Knowledge Base Settings
          </h1>
          <p className="text-xs text-slate-500">
            Configure how your public knowledge base appears to customers
          </p>
        </div>
        <PrimaryBlueButton
          label={saving ? "Saving..." : "Save Settings"}
          disabled={saving}
          onClick={() => handleSubmit()}
        />
      </div>

      {/* Info Dropdown */}
      <div className="mb-6">
        <button
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
          onClick={() => setShowHelp(!showHelp)}
        >
          <i className={`fa fa-chevron-${showHelp ? "down" : "right"} text-xs`} />
          <i className="fa fa-circle-info" />
          How KB settings work
        </button>
        {showHelp && (
          <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-slate-700 space-y-2">
            <p>
              <strong>Branding</strong> settings control the appearance of your
              public-facing knowledge base site. Customers see the company name,
              logo, theme color, and header text.
            </p>
            <p>
              <strong>Return Link</strong> lets you add a "Back to [your site]"
              link at the top of the KB, so customers can navigate back to your
              main website.
            </p>
            <p>
              <strong>Live Chat</strong> integration adds a chat widget to your
              KB pages. When enabled, customers can start a live chat directly
              from any KB article if they need more help.
            </p>
            <p>
              <strong>Bot integration:</strong> Articles with "Enable as bot response"
              can be surfaced by your AI chatbot. The bot uses the article's bot
              summary and can send a link to the full article.
            </p>
          </div>
        )}
      </div>

      {/* KB Public URL Section */}
      <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
        <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
          <i className="fa fa-globe text-blue-500" />
          Your Knowledge Base URL
        </h2>
        {domain ? (
          <div>
            <div className="flex items-center gap-3">
              <a
                href={`https://${domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
              >
                https://{domain}
              </a>
              <button
                type="button"
                className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50"
                aria-label="Copy URL"
                onClick={() => {
                  navigator.clipboard.writeText(`https://${domain}`);
                  setUrlCopied(true);
                  setTimeout(() => setUrlCopied(false), 2000);
                }}
              >
                <i className={`fa ${urlCopied ? "fa-check text-green-500" : "fa-copy"} text-sm`} />
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              This is the public URL where your customers can access your
              knowledge base. Share this link or embed it in your website.
            </p>
          </div>
        ) : (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <i className="fa fa-triangle-exclamation text-amber-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  No KB domain configured
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Contact your account manager to set up a custom domain for
                  your knowledge base (e.g., help.yourcompany.com). A DNS CNAME
                  record will need to point to our KB hosting service.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Branding Section */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-palette text-blue-500" />
            Branding
          </h2>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="company-name"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Company Name
              </label>
              <input
                type="text"
                id="company-name"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Your company name"
                value={companyName}
                onChange={(e) => setCompanyName(e.currentTarget.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Logo
              </label>
              {logoUrl && (
                <div className="flex items-center gap-3 mb-2 p-3 bg-slate-50 rounded-lg">
                  <img
                    src={logoUrl}
                    alt="KB Logo"
                    className="h-10 object-contain"
                  />
                  <button
                    type="button"
                    className="p-1.5 text-red-500 hover:text-red-700 rounded hover:bg-red-50"
                    onClick={() => setLogoUrl("")}
                    aria-label="Remove logo"
                  >
                    <i className="fa fa-trash text-sm" />
                  </button>
                </div>
              )}
              <UploadButton onChange={(file) => uploadFile(file)} />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Theme Color
              </label>
              <ColorPicker
                value={themeColor}
                onChange={(value) => setThemeColor(value)}
              />
            </div>

            <div>
              <label
                htmlFor="header-text"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Header Text
              </label>
              <input
                type="text"
                id="header-text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., How can we help you?"
                value={headerText}
                onChange={(e) => setHeaderText(e.currentTarget.value)}
              />
              <p className="text-xs text-slate-400 mt-1">
                Displayed at the top of your knowledge base homepage
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Section */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-link text-blue-500" />
            Navigation
          </h2>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="return-link-text"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Return Link Text
              </label>
              <input
                type="text"
                id="return-link-text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Back to Our Website"
                value={returnLinkText}
                onChange={(e) => setReturnLinkText(e.currentTarget.value)}
              />
            </div>

            <div>
              <label
                htmlFor="return-link-url"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Return Link URL
              </label>
              <input
                type="url"
                id="return-link-url"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://yourwebsite.com"
                value={returnLinkUrl}
                onChange={(e) => setReturnLinkUrl(e.currentTarget.value)}
              />
              <p className="text-xs text-slate-400 mt-1">
                Adds a back link at the top of your KB for customers to return
                to your main site
              </p>
            </div>
          </div>
        </div>

        {/* Live Chat Section */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-comments text-blue-500" />
            Live Chat Integration
          </h2>

          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableLiveChat}
                onChange={(e) => setEnableLiveChat(e.currentTarget.checked)}
                className="rounded border-slate-300"
              />
              <div>
                <span className="text-sm font-medium text-slate-700">
                  Enable live chat on KB pages
                </span>
                <p className="text-xs text-slate-400">
                  Show a chat widget on your knowledge base so customers can get
                  help from a live agent
                </p>
              </div>
            </label>

            {enableLiveChat && (
              <div>
                <label
                  htmlFor="live-chat-group"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Chat Team / Group
                </label>
                <select
                  id="live-chat-group"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={liveChatGroupId}
                  onChange={(e) =>
                    setLiveChatGroupId(parseInt(e.currentTarget.value))
                  }
                >
                  <option value={0}>Select a team</option>
                  {(teams ?? []).map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  Chats from KB pages will be routed to this team
                </p>
              </div>
            )}
          </div>
        </div>

      </form>

      {/* Azure Search Index Section */}
      <div className="bg-white rounded-lg border shadow-sm p-6 mt-6">
        <h2 className="text-lg font-medium text-slate-800 mb-1 flex items-center gap-2">
          <i className="fa fa-magnifying-glass text-blue-500" />
          AI Search Index
        </h2>
        <p className="text-sm text-slate-500 mb-2">
          Syncs your KB articles to Azure AI Search so bots can retrieve relevant
          content and answer customer questions automatically.
        </p>

        {/* How it works callout */}
        <div className="mb-4 p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-slate-700 space-y-2">
          <p className="font-medium text-slate-800">How the search index works</p>
          <ul className="space-y-1.5 list-none">
            <li className="flex items-start gap-2">
              <i className="fa fa-robot text-blue-500 mt-0.5 w-4 shrink-0" />
              <span>
                <strong>Bot answers:</strong> When a visitor asks your chatbot a question,
                the bot searches this index for matching articles and uses the article's
                <strong> Bot Summary</strong> field as its reply. The bot summary is a
                short, plain-language version of the article written specifically for
                bot responses — edit it per article in the Articles tab.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <i className="fa fa-file-lines text-blue-500 mt-0.5 w-4 shrink-0" />
              <span>
                <strong>What gets indexed:</strong> Article title, body content, tags,
                and bot summary. Only <em>published</em> articles marked
                "Enable as bot response" are used for bot replies; all published
                articles are searchable on the public KB site.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <i className="fa fa-bolt text-blue-500 mt-0.5 w-4 shrink-0" />
              <span>
                <strong>Automatic updates:</strong> The index updates automatically
                whenever you publish or update an article. Use <em>Reindex All</em>{" "}
                to do a full resync — useful after bulk edits or first setup.
              </span>
            </li>
          </ul>
        </div>

        {searchEnabled === null && (
          <p className="text-sm text-slate-400">Checking index status…</p>
        )}

        {searchEnabled === false && (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex items-start gap-2">
              <i className="fa fa-circle-info text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-slate-700">
                  Azure Search not configured
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  To enable search indexing, set{" "}
                  <code className="bg-slate-100 px-1 rounded text-xs">KBSearchEndpoint</code>,{" "}
                  <code className="bg-slate-100 px-1 rounded text-xs">KBSearchApiKey</code>, and{" "}
                  <code className="bg-slate-100 px-1 rounded text-xs">KBSearchIndexName</code>{" "}
                  in the Azure App Service configuration.
                </p>
              </div>
            </div>
          </div>
        )}

        {searchEnabled === true && (
          <div className="space-y-5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm font-medium text-green-700">
                Azure Search connected
              </span>
              {kbAutoReindexIntervalHours > 0 && (
                <span className="text-xs text-slate-400">
                  · auto-reindex every {kbAutoReindexIntervalHours}h
                </span>
              )}
            </div>

            {/* Bot indexing toggle — only visible if AI is enabled on this subscription */}
            {hasAI && (
              <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableKbBotIndexing}
                    onChange={(e) => setEnableKbBotIndexing(e.currentTarget.checked)}
                    className="mt-0.5 rounded border-slate-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-700">
                      Push articles to bot search index
                    </span>
                    <p className="text-xs text-slate-500 mt-0.5">
                      When checked, articles with "Enable as bot response" are automatically
                      pushed to the Azure AI Search index whenever published or updated.
                      The AI bot uses this index to answer visitor questions.
                      Uncheck to decouple your KB from the bot (public KB search still works).
                    </p>
                  </div>
                </label>
              </div>
            )}

            {!hasAI && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                <i className="fa fa-robot mr-1" />
                Bot indexing is not available on your current plan. The KB search index
                is active for public search only. Upgrade to a plan with AI to connect
                the KB to your chatbot.
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={reindexing}
                onClick={triggerReindex}
              >
                {reindexing ? (
                  <>
                    <i className="fa fa-circle-notch fa-spin" />
                    Indexing…
                  </>
                ) : (
                  <>
                    <i className="fa fa-arrow-rotate-right" />
                    Reindex All Articles
                  </>
                )}
              </button>

              {reindexResult && !reindexing && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <i className="fa fa-check-circle" />
                  {reindexResult.message}
                </span>
              )}
            </div>

            <p className="text-xs text-slate-400">
              Reindex rebuilds the full index from scratch (only bot-enabled articles).
              Runs in the background — may take a few seconds for large knowledge bases.
              Auto-reindex runs automatically on the interval above.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
